import { Injectable, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { FormsBase } from 'src/app/common-components/forms-base/forms-base';
import { containsCharactersValidator } from 'src/app/common-components/validators/contain-characters.validator';
import { RoleDetailFormValue } from '../models/role-detail-form';
import { RoleDetailSaveModel } from '../models/role-detail-save.model';
import { RoleDetailView } from '../models/role-detail-view.model';
import { getPrivileges } from '../state/role-detail.selectors';

@Injectable({
   providedIn: 'root',
})
export class RoleDetailFormService extends FormsBase implements OnDestroy {
   private destroyed$ = new Subject<void>();

   constructor(fb: FormBuilder, private store: Store, private action: Actions) {
      super();

      this.form = fb.group({
         description: fb.control(null, Validators.maxLength(255)),
         name: fb.control(null, [Validators.required, Validators.maxLength(50), containsCharactersValidator()]),
         accountId: fb.control(null),
         rolePrivileges: fb.group({}),
         roleReports: fb.control(null),
         roleRecordingExtensions: fb.control(null),
      });

      const rolePrivilegesArray = this.form.get('rolePrivileges') as FormGroup;
      this.store.select(getPrivileges).subscribe((privileges) => {
         if (privileges) {
            privileges.forEach((privilege) => {
               rolePrivilegesArray.addControl(
                  privilege?.privilegeId?.toString() ?? '',
                  fb.group({
                     rolePrivilegeId: fb.control(null),
                     privilegeId: fb.control(privilege.privilegeId),
                     isAdd: fb.control(null),
                     isDelete: fb.control(null),
                     isEdit: fb.control(null),
                     isEnabled: fb.control(null),
                     isView: fb.control(null),
                  })
               );
            });
         }
      });
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
   }

   updateRole(role: RoleDetailView): void {
      if (!role) {
         return;
      }

      this.resetRolePrivilegesFormGroup();

      const formValue: Partial<RoleDetailFormValue> = {
         description: role.description,
         name: role.name,
         accountId: role.account?.accountId,
         rolePrivileges: role.rolePrivileges,
         roleReports: role.roleReports,
         roleRecordingExtensions: role.roleRecordingExtensions,
      };
      this.form.patchValue(formValue);
      this.markAsUntouchedAndPristine();
   }

   getValue(roleId: number | null): RoleDetailSaveModel {
      const value = this.form.value as RoleDetailFormValue;
      return {
         description: value.description,
         name: value.name,
         roleId: roleId ?? 0,
         accountId: value.accountId,
         rolePrivileges: Object.values(value.rolePrivileges).map((rolePrivilege) => {
            return {
               rolePrivilegeId: rolePrivilege.rolePrivilegeId ?? 0,
               accountId: rolePrivilege.accountId,
               isAdd: rolePrivilege.isAdd,
               isDelete: rolePrivilege.isDelete,
               isEdit: rolePrivilege.isEdit,
               isEnabled: rolePrivilege.isEnabled,
               isView: rolePrivilege.isView,
               privilegeId: rolePrivilege.privilegeId,
               roleId: roleId,
            };
         }),
         roleCallExtensionRecordingPrivileges: value.roleRecordingExtensions?.extensionRecordingPermissions.map(
            (recordingPrivilege) => {
               return {
                  extensionId: recordingPrivilege.extension.extensionId,
                  isDeleted: recordingPrivilege.isDeleted,
                  isInclude: recordingPrivilege.isInclude,
                  roleCallRecordingExtensionId: recordingPrivilege.id ?? 0,
                  roleId: roleId ?? 0,
               };
            }
         ),
         roleCallExtensionGroupRecordingPrivileges:
            value.roleRecordingExtensions?.extensionGroupRecordingPermissions.map((recordingPrivilege) => {
               return {
                  extensionGroupId: recordingPrivilege.extensionGroup.extensionGroupId,
                  isDeleted: recordingPrivilege.isDeleted,
                  isInclude: recordingPrivilege.isInclude,
                  roleCallRecordingExtensionGroupId: recordingPrivilege.id ?? 0,
                  roleId: roleId ?? 0,
               };
            }),
         hasAllExtensionRecordings: value.roleRecordingExtensions?.allExtensionsIsSelected ?? false,
         roleReports: this.form.get('roleReports')?.value,
      };
   }

   resetRolePrivilegesFormGroup(): void {
      const rolePrivileges = this.form.get('rolePrivileges') as FormGroup;
      Object.keys(rolePrivileges.controls).forEach((control) => {
         const rolePrivilege = rolePrivileges.get(control) as FormGroup;
         rolePrivilege.patchValue({
            rolePrivilegeId: null,
            isAdd: null,
            isDelete: null,
            isEdit: null,
            isEnabled: null,
            isView: null,
         });
      });
   }
}
